import React from "react";
import {Link, useNavigate} from "react-router-dom";
import {useCart, cartKey} from "../context/CartContext";
import ColorSwatch from "../components/ColorSwatch";
import {calcPricing, nextOfferHint} from "../utils/pricing";
import "./Cart.css";

export default function Cart() {
  const {cart, removeFromCart, setQty, subtotal} = useCart();
  const navigate = useNavigate();

  const {shippingFee, discount, freeGift, total} = calcPricing(subtotal);
  const offerHint = nextOfferHint(subtotal);

  if (cart.length === 0) {
    return (
      <div className="cart-empty">
        <span>🛒</span>
        <h2>Your cart is empty</h2>
        <p>Looks like you haven't added anything yet.</p>
        <Link to="/products" className="btn-shop">
          Browse Products
        </Link>
      </div>
    );
  }

  function handleQtyChange(item, newQty) {
    if (newQty < 1) {
      // Remove item instead of setting qty to 0
      removeFromCart(cartKey(item));
    } else {
      setQty(cartKey(item), newQty);
    }
  }

  return (
    <div className="cart-page">
      <h1 className="cart-title">
        Your Cart ({cart.reduce((s, i) => s + i.qty, 0)} item
        {cart.reduce((s, i) => s + i.qty, 0) !== 1 ? "s" : ""})
      </h1>

      <div className="cart-layout">
        <div className="cart-items">
          {cart.map((item) => (
            <div key={cartKey(item)} className="cart-item">
              <img
                src={
                  item.images?.[0]?.url ||
                  item.images?.[0] ||
                  "https://placehold.co/80x80/f5f5f5/aaa?text="
                }
                alt={item.name}
                className="cart-item-img"
              />
              <div className="cart-item-info">
                <Link
                  to={`/products/${item.slug || item._id}`}
                  className="cart-item-name"
                >
                  {item.name}
                </Link>
                {item.customName && (
                  <p className="cart-item-custom">Name: {item.customName}</p>
                )}
                {item.color && (
                  <p className="cart-item-custom">
                    Colour: <ColorSwatch color={item.color} />
                  </p>
                )}
                <p className="cart-item-price">
                  ₹{item.price.toLocaleString("en-IN")} each
                </p>
              </div>

              <div className="cart-item-controls">
                <div
                  className="qty-control"
                  style={item.customName ? {visibility: "hidden"} : undefined}
                >
                  <button
                    onClick={() => handleQtyChange(item, item.qty - 1)}
                    title={item.qty === 1 ? "Remove item" : "Decrease quantity"}
                  >
                    {item.qty === 1 ? "🗑️" : "−"}
                  </button>
                  <span>{item.qty}</span>
                  <button onClick={() => handleQtyChange(item, item.qty + 1)}>
                    +
                  </button>
                </div>
                <span className="cart-item-total">
                  ₹{(item.price * item.qty).toLocaleString("en-IN")}
                </span>
                <button
                  className="remove-btn"
                  onClick={() => removeFromCart(cartKey(item))}
                  title="Remove item"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Order summary */}
        <aside className="cart-summary">
          <h3>Order Summary</h3>
          <div className="summary-row">
            <span>Subtotal ({cart.reduce((s, i) => s + i.qty, 0)} items)</span>
            <span>₹{subtotal.toLocaleString("en-IN")}</span>
          </div>
          <div className="summary-row">
            <span>Shipping</span>
            <span>
              {shippingFee === 0 ? (
                <span className="free">FREE</span>
              ) : (
                `₹${shippingFee}`
              )}
            </span>
          </div>
          {discount > 0 && (
            <div className="summary-row">
              <span>Discount (10% off)</span>
              <span className="free">−₹{discount.toLocaleString("en-IN")}</span>
            </div>
          )}
          {freeGift && (
            <div className="summary-row">
              <span>🎁 Surprise gift</span>
              <span className="free">FREE</span>
            </div>
          )}
          {offerHint && <p className="free-shipping-msg">{offerHint}</p>}
          <div className="summary-total">
            <span>Total</span>
            <span>₹{total.toLocaleString("en-IN")}</span>
          </div>
          <button
            className="btn-checkout"
            onClick={() => navigate("/checkout")}
          >
            Proceed to Checkout →
          </button>
          <Link to="/products" className="continue-link">
            ← Continue shopping
          </Link>
        </aside>
      </div>
    </div>
  );
}
